// The playback transport: pick a window, press play, and history replays on
// the map — events pop in with the fresh pulse as the playhead passes their
// occurrence, then age through the same colour ramp the live map uses
// (when.ts's ramps take the playhead as `now`). Speeds are map-time
// multipliers over real time.
//
// THE DEFAULT IS THE LIVE MAP RUN FAST ("as live"): the marker style's own
// announcement, the age ramp over the configured colour hours, the fade over
// the fade hours, one marker per situation, and an event LEAVES when the live
// map would have dropped it. The fixed stays ("pulse only", "stay 1 h", …)
// are the other grammar — each event an individual moment that lingers a set
// map-time and goes. Two things are pinned to REAL time whatever the speed,
// because a 10-minute announcement is one tick at ×3600: the splash ring
// (SPLASH_REAL_MS) and a floor under every marker announcement
// (ANNOUNCE_REAL_MS), both passed to the bake as speed × a constant.
//
// The track is a real TIMELINE widget, not a slider: a full-width canvas with
// a time scale, a tick per event occurrence, the replay window as a band with
// DRAGGABLE start/end handles (dragging an edge of a preset window turns it
// into a clip), a draggable playhead, wheel/pinch ZOOM around the cursor and
// drag-to-pan outside the band. The Clip popover stays for exact datetimes;
// the handles are the fast version of the same thing. ⟲ loops the window.
//
// TWO SURFACES, TWO CADENCES. Events are client-side GeoJSON, so they re-bake
// per tick for free (objects.setPlaybackTime). Objects and damage live in
// tiles, where time travel is a ?asof fetch — so the tile clock follows the
// playhead COARSELY: only when it crosses a 30-minute bucket, debounced,
// and never within 30 minutes of real now (that is the live tile set, which
// is the cacheable one). Per-frame asof would mint a Martin cache key per
// animation frame and melt the tile cache for zero visible gain — damage
// markers do not move minute to minute.

import * as maplibregl from 'maplibre-gl';
import * as aircraft from './aircraft';
import { kindGroups, LIVE_KINDS } from './livekinds';
import { type ColorsPopover, createColorsPopover } from './colorpick';
import { liveEventTimes, type PlaybackOpts, ReplayLayers, setPlaybackTime,
         setEntitiesAsof, setPlaybackView, setReplayLayers } from './objects';
import { liveFadeHours } from './when';

const WINDOWS: [string, number][] = [
  ['1 h', 1], ['4 h', 4], ['12 h', 12], ['24 h', 24],
  ['3 d', 72], ['7 d', 168], ['30 d', 720],
];
// Map-time over real time, roughly "a 24 h window in 24 min / 5 min / 96 s /
// 24 s".
const SPEEDS: [string, number][] = [['×60', 60], ['×300', 300], ['×900', 900],
                                    ['×3600', 3600]];
// What an event does after it lands, [label, value]. 'live' is the live
// grammar (header comment): the default, because "play it back" means the
// live map run fast, and a map where each event is a 2.5-second blip is not
// that. 'pulse' ties the stay to the splash ring (speed-scaled ~2.5 real
// seconds): one ring and gone. A number is a fixed map-time stay under the
// linger arc; 'inf' accumulates to the end of the window.
const STAYS: [string, string][] = [
  ['as live', 'live'], ['pulse only', 'pulse'], ['stay 15 m', '900000'],
  ['stay 1 h', '3600000'], ['stay 3 h', '10800000'], ['stay 12 h', '43200000'],
  ['stay 24 h', '86400000'], ['stay to end', 'inf'],
];
const STAY_KEY = 'cockpit-playback-stay';
const WINDOW_KEY = 'cockpit-playback-window';
// Replay's own layer choices — independent of the Layers panel by design.
const LAYERS_KEY = 'cockpit-playback-layers';
const LAYER_ROWS: [Exclude<keyof ReplayLayers, 'kinds'>, string][] = [
  ['events', 'Events'],
  ['damage', 'Damage'],
  ['control', 'Territorial control'],
  ['aircraft', 'Aircraft'],
  ['context', 'Areas & lines'],
  ['nowPicture', 'Places & units'],
];
const TICK_MS = 120;
// Every splash ring lives this long in REAL time at any speed — the map-time
// window passed to the bake is speed × this.
const SPLASH_REAL_MS = 2_500;
// Every marker announcement (arrival ring, halo, pulse) lasts at least this
// long in REAL time — the same trick, for the same reason.
const ANNOUNCE_REAL_MS = 1_500;
const ASOF_BUCKET_MS = 30 * 60_000;
const ASOF_LIVE_BAND_MS = 30 * 60_000;

// Zoom bounds for the visible domain, and the narrowest window the handles
// can pinch the replay down to.
const MIN_SPAN_MS = 5 * 60_000;
const MAX_SPAN_MS = 90 * 86_400_000;
const MIN_WINDOW_MS = 60_000;
const HANDLE_PX = 8;

let bar: HTMLElement | null = null;
let canvas: HTMLCanvasElement | null = null;
let mapRef: maplibregl.Map | null = null;
// Storage reads guarded: a private window or a blocked origin must not take
// the transport down at import.
function stored(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function store(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* nothing to remember in */ }
}

// A day at ×900 is a minute and a half — long enough that a wave reads as a
// wave, short enough to sit through. The old 4 h / ×300 default replayed the
// last four hours in 48 s, and on a quiet afternoon that is an empty map.
let windowH = Number(stored(WINDOW_KEY)) || 24;
let speedIdx = 2;                       // ×900 — a 24 h window in ~96 s
let stayVal = STAYS.some(([, v]) => v === stored(STAY_KEY))
  ? stored(STAY_KEY)! : 'live';

function loadReplayLayers(): ReplayLayers {
  // Events only by default — replay exists to watch things happen; damage,
  // context geometry and the now-picture are opt-in. Territorial control is
  // the exception: it is drawn AS OF the playhead, so a replay across a
  // capture is the district changing hands, and that is the point of it.
  const base: ReplayLayers =
    { events: true, damage: false, context: false, nowPicture: false,
      aircraft: false, control: true, kinds: {} };
  try {
    const saved = JSON.parse(stored(LAYERS_KEY) || '{}');
    for (const [key] of LAYER_ROWS) {
      if (typeof saved[key] === 'boolean') base[key] = saved[key];
    }
    for (const k of Object.keys(LIVE_KINDS)) {
      if (saved.kinds?.[k] === false) base.kinds[k] = false;
    }
  } catch { /* a corrupt blob means defaults */ }
  return base;
}

let replayLayers = loadReplayLayers();

/** The Events row's kind filter: all/none, then one group per KIND_GROUPS
 *  with a summarising head checkbox and an "only" button — "show me the
 *  launches and what answered them" is one click, not eleven. Kind boxes
 *  carry `data-in` (their group); heads carry `data-group`. */
function kindsMarkup(): string {
  const groups = kindGroups();
  return `<div class="tt-kindtools"><span>kinds</span>
      <button type="button" class="tt-mini" data-kinds="all" title="Show every kind">all</button>
      <button type="button" class="tt-mini" data-kinds="none" title="Hide every kind — then pick what to watch">none</button>
    </div>` + groups.map((g) =>
    `<div class="tt-kgroup">
      <div class="tt-kghead">
        <label class="tt-lyr"><input type="checkbox" data-group="${g.key}" /> ${g.label}</label>
        <button type="button" class="tt-mini" data-solo="${g.key}" title="Only this group">only</button>
      </div>
      <div class="tt-kinds">${g.kinds.map((k) => {
        const v = LIVE_KINDS[k]!;
        return `<label class="tt-lyr tt-kind"><input type="checkbox" data-kind="${k}" data-in="${g.key}"${
          replayLayers.kinds[k] !== false ? ' checked' : ''} /> ${v.glyph} ${v.label}</label>`;
      }).join('')}</div>
    </div>`).join('');
}
let t1 = 0;                             // window end (fixed when opened/changed)
let clip: { from: number; to: number } | null = null;
let looping = false;
let playhead: number | null = null;     // null = live
let playing = false;
let ticker: number | undefined;
let lastTick = 0;
let asofBucket: number | null = null;
let asofTimer: number | undefined;
let scaleTimer: number | undefined;
let escHandler: ((ev: KeyboardEvent) => void) | null = null;
let outsideHandler: ((ev: MouseEvent) => void) | null = null;
let colorsPopover: ColorsPopover | null = null;

// The visible domain of the track — wider than the window, zoomable. While
// the user has not zoomed or panned, it auto-fits the window each draw.
let dom0 = 0;
let dom1 = 0;
let userZoomed = false;
let drag: 'start' | 'end' | 'move' | 'scrub' | 'pan' | 'pinch' | null = null;
let panX = 0;
let panD0 = 0;
let panD1 = 0;
// Window-move drag anchor: the window and playhead as they were at
// pointerdown, so the whole band slides rigidly with the pointer.
let moveStart = { x: 0, from: 0, to: 0, ph: null as number | null };
// The top strip of the band is the MOVE grip; below it, clicks seek.
const MOVE_STRIP_PX = 11;
const touches = new Map<number, number>();   // pointerId -> x, for pinch
let pinchDist = 0;

export function isTimeTravelling(): boolean {
  return bar !== null;
}

export function closeTimeline(map: maplibregl.Map): void {
  stop();
  playhead = null;
  clip = null;
  looping = false;
  userZoomed = false;
  setPlaybackTime(map, null);
  aircraft.setReplay(map, false, 0, 0);  // the live overlay's own state returns
  setPlaybackView(map, false);           // the normal map state comes back
  // Directly, not through the debounced path — the debounce fires after
  // mapRef is nulled and would leave the tiles stuck in the past.
  window.clearTimeout(asofTimer);
  window.clearInterval(scaleTimer);
  if (escHandler) document.removeEventListener('keydown', escHandler, true);
  if (outsideHandler) document.removeEventListener('pointerdown', outsideHandler);
  escHandler = null;
  outsideHandler = null;
  asofBucket = null;
  setAsof(map, null);
  bar?.remove();
  bar = null;
  canvas = null;
  colorsPopover = null;   // its element left with the bar
  mapRef = null;
}

export function openTimeline(map: maplibregl.Map): void {
  if (bar) return;
  mapRef = map;
  t1 = Date.now();
  userZoomed = false;
  // Replay is its own view: the "now" clutter steps back so the time-aware
  // things (events, damage) read. Own state — including its own layer
  // choices — restored exactly on close.
  setReplayLayers(map, replayLayers);
  setPlaybackView(map, true);
  bar = document.createElement('div');
  bar.className = 'pod timebar';
  bar.innerHTML = `
    <div class="tt-controls">
      <select id="tt-win" title="How far back the replay window reaches">${
        WINDOWS.map(([label, h]) =>
          `<option value="${h}"${h === windowH ? ' selected' : ''}>${label}</option>`).join('')}
        <option value="clip" hidden>Clip</option>
      </select>
      <span class="tt-count" id="tt-count" title="Events inside the replay window — drag the handles or widen the window to reach more"></span>
      <button class="now" id="tt-restart" title="Jump to the start of the window">⏮</button>
      <button class="now" id="tt-play" title="Play the window — events appear and age as the clock runs">▶</button>
      <button class="now" id="tt-speed" title="Playback speed over real time">${
        SPEEDS[speedIdx]![0]}</button>
      <button class="now" id="tt-loop" title="Loop: restart from the beginning when the replay ends">⟲</button>
      <select id="tt-stay" title="What each event does after it lands (map time). As live = the live map's own timing run fast: flash, colour by age, fade and drop past the fade window, one marker per situation. Pulse only = one ring and gone. Stay = a fixed time on the map, then gone.">${
        STAYS.map(([label, v]) =>
          `<option value="${v}"${v === stayVal ? ' selected' : ''}>${label}</option>`).join('')}
      </select>
      <button class="now" id="tt-clip" title="Replay an exact time range">Clip</button>
      <button class="now" id="tt-layers" title="What the replay shows — its own choices, separate from the Layers panel">Layers</button>
      <button class="now" id="tt-colors" title="Ring colours by faction or attack-vs-impact — applies to the live map too">Colours</button>
      <span class="when" id="tt-when">live</span>
      <button class="now" id="tt-now" title="Back to the live picture">Live</button>
      <button class="now" id="tt-fold" title="Collapse the bar — the replay keeps running over a clear map">▾</button>
    </div>
    <div class="tt-track" id="tt-track">
      <canvas id="tt-canvas"></canvas>
      <div class="tt-hover" id="tt-hover" hidden></div>
    </div>
    <div class="tt-clipbox" id="tt-clipbox" hidden>
      <div class="tt-boxhead">Clip
        <button class="tt-x" data-close="1" title="Close">✕</button></div>
      <label>From <input type="datetime-local" id="tt-from" /></label>
      <label>To <input type="datetime-local" id="tt-to" /></label>
      <div class="row">
        <button class="now" id="tt-clip-clear">Clear</button>
        <button class="now" id="tt-clip-play">▶ Play clip</button>
      </div>
    </div>
    <div class="tt-clipbox tt-layersbox" id="tt-layersbox" hidden>
      <div class="tt-boxhead">Replay layers
        <button class="tt-x" data-close="1" title="Close">✕</button></div>${
      LAYER_ROWS.map(([key, label]) =>
        `<label class="tt-lyr"><input type="checkbox" data-lyr="${key}"${
          replayLayers[key] ? ' checked' : ''} /> ${label}</label>`
        + (key === 'events'
          ? kindsMarkup()
          : key === 'aircraft'
            ? `<div class="tt-lyrnote">flight paths from the ADS-B record — the last 7 days</div>`
            : '')).join('')}
    </div>`;
  document.querySelector('.cockpit')!.appendChild(bar);
  canvas = bar.querySelector('#tt-canvas') as HTMLCanvasElement;

  const playBtn = bar.querySelector('#tt-play') as HTMLButtonElement;
  const clipbox = bar.querySelector('#tt-clipbox') as HTMLElement;

  (bar.querySelector('#tt-win') as HTMLSelectElement).addEventListener('change', (ev) => {
    const v = (ev.target as HTMLSelectElement).value;
    if (v === 'clip') return;            // the hidden badge, never picked by hand
    dropClip();
    windowH = Number(v) || 4;
    store(WINDOW_KEY, String(windowH));
    t1 = Date.now();
    userZoomed = false;
    syncClipInputs();
    if (playhead !== null) {
      playhead = Math.max(t0(), Math.min(playhead, t1));
    }
    apply();
  });
  playBtn.addEventListener('click', () => (playing ? stop() : play()));
  bar.querySelector('#tt-restart')!.addEventListener('click', () => {
    if (!clip) t1 = Date.now();
    playhead = t0();
    apply();
  });
  bar.querySelector('#tt-speed')!.addEventListener('click', (ev) => {
    speedIdx = (speedIdx + 1) % SPEEDS.length;
    (ev.target as HTMLElement).textContent = SPEEDS[speedIdx]![0];
  });
  bar.querySelector('#tt-loop')!.addEventListener('click', (ev) => {
    looping = !looping;
    (ev.currentTarget as HTMLElement).classList.toggle('on', looping);
  });
  (bar.querySelector('#tt-stay') as HTMLSelectElement).addEventListener('change', (ev) => {
    stayVal = (ev.target as HTMLSelectElement).value;
    store(STAY_KEY, stayVal);
    apply();
  });
  const layersbox = bar.querySelector('#tt-layersbox') as HTMLElement;
  colorsPopover = createColorsPopover(bar, () => mapRef);
  const colorsbox = colorsPopover.el;
  // The popovers live in three FIXED slots — Layers left, Colours centre,
  // Clip right — so no pair can ever cover the other, and each stays where
  // the eye last found it. Opening one closes the rest.
  const closeOthers = (keep: HTMLElement | null): void => {
    for (const box of [clipbox, layersbox, colorsbox]) {
      if (box !== keep) box.hidden = true;
    }
  };
  bar.querySelector('#tt-clip')!.addEventListener('click', () => {
    closeOthers(clipbox);
    if (!clipbox.hidden) { clipbox.hidden = true; return; }
    syncClipInputs();
    clipbox.hidden = false;
  });
  bar.querySelector('#tt-layers')!.addEventListener('click', () => {
    closeOthers(layersbox);
    layersbox.hidden = !layersbox.hidden;
  });
  bar.querySelector('#tt-colors')!.addEventListener('click', () => {
    closeOthers(colorsbox);
    colorsPopover!.toggle();
  });
  layersbox.addEventListener('click', (ev) => {
    if ((ev.target as HTMLElement).closest('[data-close]')) layersbox.hidden = true;
  });
  clipbox.addEventListener('click', (ev) => {
    if ((ev.target as HTMLElement).closest('[data-close]')) clipbox.hidden = true;
  });
  // Collapse to a thin strip: the transport keeps running, the map clears.
  bar.querySelector('#tt-fold')!.addEventListener('click', () => {
    closeOthers(clipbox);
    clipbox.hidden = true;
    bar!.classList.add('folded');
  });
  bar.querySelector('#tt-track')!.addEventListener('click', () => {
    if (bar!.classList.contains('folded')) {
      bar!.classList.remove('folded');
      draw();
    }
  });
  // Esc closes an open popover and NOTHING else — it does not fold the bar
  // and never closes the timeline. Esc is shared with the draw flow, the
  // selection card and the console, so this consumes the key only when it
  // genuinely had something of its own to close; otherwise it stays out of
  // the way entirely.
  escHandler = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || !bar) return;
    const open = [clipbox, layersbox, colorsbox].find((b) => !b.hidden);
    if (!open) return;
    open.hidden = true;
    ev.stopPropagation();
  };
  document.addEventListener('keydown', escHandler, true);
  // A click on the map dismisses whatever popover is open — the popovers
  // float over the map, so "click away" is the gesture people try first.
  outsideHandler = (ev: MouseEvent) => {
    if (!bar || bar.contains(ev.target as Node)) return;
    closeOthers(null);
  };
  document.addEventListener('pointerdown', outsideHandler);

  // The kind filter is read from the CHECKBOXES, never the other way round:
  // group heads, "all"/"none" and "only" all just set boxes and commit, so
  // there is one path from the popover to replayLayers.
  const kindBoxes = () =>
    [...layersbox.querySelectorAll<HTMLInputElement>('input[data-kind]')];
  // A group head SUMMARISES its members — checked when all are on,
  // indeterminate when some are — and is never itself stored.
  const syncHeads = () => {
    for (const head of layersbox.querySelectorAll<HTMLInputElement>('input[data-group]')) {
      const members = kindBoxes().filter((cb) => cb.dataset.in === head.dataset.group);
      const on = members.filter((cb) => cb.checked).length;
      head.checked = members.length > 0 && on === members.length;
      head.indeterminate = on > 0 && on < members.length;
    }
  };
  const commit = () => {
    for (const cb of layersbox.querySelectorAll<HTMLInputElement>('input[data-lyr]')) {
      replayLayers[cb.dataset.lyr as Exclude<keyof ReplayLayers, 'kinds'>] = cb.checked;
    }
    for (const cb of kindBoxes()) replayLayers.kinds[cb.dataset.kind!] = cb.checked;
    syncHeads();
    store(LAYERS_KEY, JSON.stringify(replayLayers));
    if (mapRef) setReplayLayers(mapRef, replayLayers);
    apply();   // the aircraft toggle is applied by the transport, not objects.ts
  };
  const setKinds = (pick: (cb: HTMLInputElement) => boolean) => {
    for (const cb of kindBoxes()) cb.checked = pick(cb);
    commit();
  };
  layersbox.addEventListener('change', (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.dataset.group) {   // a group head sets every member to its own state
      setKinds((cb) => (cb.dataset.in === t.dataset.group ? t.checked : cb.checked));
      return;
    }
    commit();
  });
  layersbox.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement)
      .closest<HTMLElement>('button[data-kinds], button[data-solo]');
    if (!btn) return;
    ev.preventDefault();
    if (btn.dataset.kinds) setKinds(() => btn.dataset.kinds === 'all');
    else setKinds((cb) => cb.dataset.in === btn.dataset.solo);
  });
  syncHeads();
  bar.querySelector('#tt-clip-play')!.addEventListener('click', () => {
    const from = new Date(
      (clipbox.querySelector('#tt-from') as HTMLInputElement).value).getTime();
    // A future end is an empty tail, not an error — just trim it to now.
    const to = Math.min(new Date(
      (clipbox.querySelector('#tt-to') as HTMLInputElement).value).getTime(), Date.now());
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
    stop();
    clip = { from, to };
    t1 = to;
    markClip();
    userZoomed = false;
    clipbox.hidden = true;
    playhead = from;
    apply();
    play();
  });
  bar.querySelector('#tt-clip-clear')!.addEventListener('click', () => {
    clipbox.hidden = true;
    stop();
    dropClip();
    goLive();
  });
  bar.querySelector('#tt-now')!.addEventListener('click', () => {
    stop();
    dropClip();
    goLive();
  });

  installTrackHandlers();

  // At live the window's end slides with the wall clock; refresh so an
  // hour-old bar does not still claim its right edge is now. Also picks up
  // events that arrived while idle.
  scaleTimer = window.setInterval(() => {
    if (playhead === null && !clip) t1 = Date.now();
    draw();
  }, 15_000);
  apply();   // not draw(): the flight record loads for the window at once
}

const t0 = (): number => (clip ? clip.from : t1 - windowH * 3_600_000);

function markClip(): void {
  const win = bar?.querySelector('#tt-win') as HTMLSelectElement | null;
  if (win) win.value = 'clip';
  bar?.querySelector('#tt-clip')?.classList.add('on');
}

function dropClip(): void {
  if (!clip) return;
  clip = null;
  const win = bar?.querySelector('#tt-win') as HTMLSelectElement | null;
  if (win) win.value = String(windowH);
  bar?.querySelector('#tt-clip')?.classList.remove('on');
  t1 = Date.now();
  userZoomed = false;
  syncClipInputs();
  draw();
}

// The Clip popover's from/to always mirror the actual window, so dragging a
// handle with the popover open shows the numbers move — one window, two views.
function syncClipInputs(): void {
  const from = bar?.querySelector('#tt-from') as HTMLInputElement | null;
  const to = bar?.querySelector('#tt-to') as HTMLInputElement | null;
  if (from) from.value = toLocalInput(t0());
  if (to) to.value = toLocalInput(t1);
}

// --- track interaction -------------------------------------------------------

const xOf = (t: number, w: number): number => ((t - dom0) / (dom1 - dom0)) * w;
const tOf = (x: number, w: number): number => dom0 + (x / w) * (dom1 - dom0);

function fitDomain(): void {
  const span = Math.max(t1 - t0(), MIN_WINDOW_MS);
  dom0 = t0() - span * 0.15;
  dom1 = t1 + span * 0.15;
}

function zoomAt(tc: number, factor: number): void {
  const span = dom1 - dom0;
  const next = Math.min(MAX_SPAN_MS, Math.max(MIN_SPAN_MS, span * factor));
  const scale = next / span;
  dom0 = tc - (tc - dom0) * scale;
  dom1 = dom0 + next;
  userZoomed = true;
  draw();
}

function setStart(t: number): void {
  const to = t1;
  const from = Math.min(t, to - MIN_WINDOW_MS);
  clip = { from, to };
  markClip();
  syncClipInputs();
  if (playhead !== null) playhead = Math.max(playhead, from);
  apply();
}

function setEnd(t: number): void {
  const from = t0();
  const to = Math.max(Math.min(t, Date.now()), from + MIN_WINDOW_MS);
  clip = { from, to };
  t1 = to;
  markClip();
  syncClipInputs();
  if (playhead !== null) playhead = Math.min(playhead, to);
  apply();
}

// Slide the whole window rigidly — span fixed, playhead riding along at the
// same relative position, the right edge never past now.
function shiftWindow(dt: number): void {
  const span = moveStart.to - moveStart.from;
  const to = Math.min(moveStart.to + dt, Date.now());
  const from = to - span;
  clip = { from, to };
  t1 = to;
  markClip();
  syncClipInputs();
  if (moveStart.ph !== null) {
    playhead = Math.min(Math.max(moveStart.ph + (to - moveStart.to), from), to);
  }
  apply();
}

function scrubTo(t: number): void {
  // A stale end would hide fresh events — but a clip's end is the clip's end.
  if (!clip) {
    t1 = Math.max(t1, Date.now() - 1);
    if (t >= t1 - 1000) { goLive(); return; }
  }
  playhead = Math.min(Math.max(t, t0()), t1);
  apply();
}

function installTrackHandlers(): void {
  const cv = canvas!;
  const hover = bar!.querySelector('#tt-hover') as HTMLElement;

  cv.addEventListener('pointerdown', (ev) => {
    // Folded, the track is a restore affordance, not a scrubber — let the
    // click bubble to #tt-track and bring the controls back.
    if (bar?.classList.contains('folded')) return;
    cv.setPointerCapture(ev.pointerId);
    touches.set(ev.pointerId, ev.offsetX);
    if (touches.size === 2) {
      const [a, b] = [...touches.values()];
      drag = 'pinch';
      pinchDist = Math.abs(a! - b!);
      return;
    }
    const w = cv.clientWidth;
    const x = ev.offsetX;
    const y = ev.offsetY;
    const xs = xOf(t0(), w);
    const xe = xOf(t1, w);
    const xp = playhead !== null ? xOf(playhead, w) : null;
    if (Math.abs(x - xs) <= HANDLE_PX) {
      drag = 'start'; userZoomed = true;
    } else if (Math.abs(x - xe) <= HANDLE_PX) {
      drag = 'end'; userZoomed = true;
    } else if (xp !== null && Math.abs(x - xp) <= 6) {
      drag = 'scrub';
    } else if (y <= MOVE_STRIP_PX && x > xs && x < xe) {
      // The grip strip across the band's top: slide the whole window.
      drag = 'move';
      moveStart = { x, from: t0(), to: t1, ph: playhead };
      userZoomed = true;
    } else if (x > xs && x < xe) {
      drag = 'scrub';
      scrubTo(tOf(x, w));
    } else {
      drag = 'pan';
      panX = x; panD0 = dom0; panD1 = dom1;
      userZoomed = true;
    }
  });

  cv.addEventListener('pointermove', (ev) => {
    const w = cv.clientWidth;
    if (!w) return;
    const x = ev.offsetX;
    if (touches.has(ev.pointerId)) touches.set(ev.pointerId, x);
    if (drag === 'pinch' && touches.size === 2) {
      hover.hidden = true;
      const [a, b] = [...touches.values()];
      const dist = Math.max(Math.abs(a! - b!), 8);
      if (pinchDist > 0) zoomAt(tOf((a! + b!) / 2, w), pinchDist / dist);
      pinchDist = dist;
      return;
    }
    if (drag === 'start') { setStart(tOf(x, w)); }
    else if (drag === 'end') { setEnd(tOf(x, w)); }
    else if (drag === 'move') { shiftWindow(((x - moveStart.x) / w) * (dom1 - dom0)); }
    else if (drag === 'scrub') { scrubTo(tOf(x, w)); }
    else if (drag === 'pan') {
      const shift = ((panX - x) / w) * (panD1 - panD0);
      dom0 = panD0 + shift;
      dom1 = panD1 + shift;
      draw();
    }
    // Cursor affordance + hover time bubble.
    const y = ev.offsetY;
    const xs = xOf(t0(), w);
    const xe = xOf(t1, w);
    const xp = playhead !== null ? xOf(playhead, w) : null;
    cv.style.cursor =
      drag === 'pan' ? 'grabbing'
      : drag === 'move' ? 'move'
      : (Math.abs(x - xs) <= HANDLE_PX || Math.abs(x - xe) <= HANDLE_PX) ? 'ew-resize'
      : (xp !== null && Math.abs(x - xp) <= 6) ? 'ew-resize'
      : (y <= MOVE_STRIP_PX && x > xs && x < xe) ? 'move'
      : (x > xs && x < xe) ? 'pointer' : 'grab';
    if (drag !== 'pinch' && ev.pointerType !== 'touch') {
      hover.hidden = false;
      hover.style.left = `${x}px`;
      hover.textContent = fmtInstant(tOf(x, w));
    }
  });

  const release = (ev: PointerEvent): void => {
    touches.delete(ev.pointerId);
    if (touches.size < 2 && drag === 'pinch') drag = null;
    if (touches.size === 0) drag = null;
    pinchDist = 0;
  };
  cv.addEventListener('pointerup', release);
  cv.addEventListener('pointercancel', release);
  cv.addEventListener('pointerleave', () => { hover.hidden = true; });

  cv.addEventListener('wheel', (ev) => {
    ev.preventDefault();
    const w = cv.clientWidth;
    if (!w) return;
    if (ev.shiftKey || Math.abs(ev.deltaX) > Math.abs(ev.deltaY)) {
      const d = ev.shiftKey ? ev.deltaY : ev.deltaX;
      const shift = (d / w) * (dom1 - dom0);
      dom0 += shift;
      dom1 += shift;
      userZoomed = true;
      draw();
    } else {
      zoomAt(tOf(ev.offsetX, w), Math.exp(ev.deltaY * 0.0016));
    }
  }, { passive: false });
}

// --- transport ---------------------------------------------------------------

function play(): void {
  if (!bar) return;
  if (playhead === null || playhead >= t1 - 1000) {
    if (!clip) t1 = Date.now();
    playhead = t0();
  }
  playing = true;
  lastTick = performance.now();
  (bar.querySelector('#tt-play') as HTMLElement).textContent = '⏸';
  ticker = window.setInterval(() => {
    const now = performance.now();
    const dt = now - lastTick;
    lastTick = now;
    playhead = (playhead ?? t0()) + dt * SPEEDS[speedIdx]![1];
    if (playhead >= t1) {
      if (looping) {
        playhead = t0();                 // replay the window from the top
      } else {
        playhead = t1;
        stop();                          // pause at the end, stay in history
      }
    }
    apply();
  }, TICK_MS);
}

function stop(): void {
  playing = false;
  window.clearInterval(ticker);
  ticker = undefined;
  const btn = bar?.querySelector('#tt-play');
  if (btn) btn.textContent = '▶';
}

function goLive(): void {
  playhead = null;
  apply();
}

/** The bake options for a stay setting at a speed — pure, so the mapping
 *  from the select to what the map does is a test rather than a screenshot.
 *
 *  'live' is the live map run fast: the live grammar in map time, an event
 *  leaving when the live map would have dropped it (the fade window; never,
 *  when fading is off). 'pulse' is one splash and gone. A number is a fixed
 *  map-time stay under the linger arc; 'inf' keeps every event to the end.
 *  The splash and the announcement floor scale with SPEED alone. */
export function playbackOptsFor(stay: string, speed: number, from: number,
                                fadeHours: number): PlaybackOpts {
  const splashMs = speed * SPLASH_REAL_MS;
  const announceFloorMs = speed * ANNOUNCE_REAL_MS;
  if (stay === 'live') {
    return { splashMs, from, announceFloorMs, grammar: 'live',
             lingerMs: fadeHours > 0 ? fadeHours * 3_600_000 : Infinity };
  }
  return { splashMs, from, announceFloorMs, grammar: 'linger',
           lingerMs: stay === 'pulse' ? splashMs
             : stay === 'inf' ? Infinity : Number(stay) };
}

function apply(): void {
  const map = mapRef;
  if (!map || !bar) return;
  const when = bar.querySelector('#tt-when') as HTMLElement;

  // Events, every tick, client-side: replay draws ONLY events inside the
  // window, each staying as the stay select says — as live by default.
  setPlaybackTime(map, playhead,
    playbackOptsFor(stayVal, SPEEDS[speedIdx]![1], t0(), liveFadeHours()));
  // Flight paths at the same cadence, from the window's record loaded once
  // (aircraft.ts's replay section) — the transport's own toggle, so a replay
  // of last night's tanker orbit needs no live overlay on.
  aircraft.setReplay(map, replayLayers.aircraft, t0(), t1);
  aircraft.setReplayAt(playhead);

  if (playhead === null) {
    when.textContent = 'live';
    scheduleAsof(null);
    draw();
    return;
  }
  const back = Date.now() - playhead;
  const backTxt = back >= 86_400_000
    ? `−${Math.floor(back / 86_400_000)}d ${Math.round((back % 86_400_000) / 3_600_000)}h`
    : back >= 3_600_000
      ? `−${Math.floor(back / 3_600_000)}h ${Math.round((back % 3_600_000) / 60_000)}m`
      : `−${Math.max(1, Math.round(back / 60_000))}m`;
  when.textContent = `${new Date(playhead).toLocaleString(undefined, {
    weekday: 'short', hour: '2-digit', minute: '2-digit' })} · ${backTxt}`;

  // The tile clock: coarse on purpose (header comment). Inside the live band
  // the asof is dropped entirely so the map serves the cacheable tile set.
  scheduleAsof(Date.now() - playhead < ASOF_LIVE_BAND_MS
    ? null : Math.floor(playhead / ASOF_BUCKET_MS));
  draw();
}

// --- drawing -----------------------------------------------------------------

function themeColor(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function draw(): void {
  if (!bar || !canvas) return;
  if (!userZoomed) fitDomain();
  // What the window holds, in words — an empty window plays an empty map,
  // and "nothing happened" has to be distinguishable from "nothing works".
  const count = bar.querySelector('#tt-count') as HTMLElement | null;
  if (count) {
    const lo = t0();
    const hi = t1;
    const n = liveEventTimes().filter((x) => x >= lo && x <= hi).length;
    count.textContent = n ? `${n} event${n === 1 ? '' : 's'}` : 'no events in window';
    count.classList.toggle('empty', n === 0);
  }
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return;

  // Follow the playhead when zoomed in and playing, keeping it on screen.
  if (playing && playhead !== null && userZoomed) {
    const span = dom1 - dom0;
    if (playhead > dom1 - span * 0.06 || playhead < dom0) {
      dom0 = playhead - span * 0.5;
      dom1 = dom0 + span;
    }
  }

  const dpr = devicePixelRatio || 1;
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const ink = themeColor('--ink', '#26333a');
  const dim = themeColor('--dim', '#7d8b92');
  const accent = themeColor('--accent', '#087f78');
  const now = Date.now();
  const labelH = 14;
  const trackH = h - labelH;

  // Track background; the future is visibly dead space.
  ctx.fillStyle = dim;
  ctx.globalAlpha = 0.10;
  ctx.fillRect(0, 0, w, trackH);
  const xNow = xOf(now, w);
  if (xNow < w) {
    ctx.globalAlpha = 0.14;
    ctx.fillRect(Math.max(xNow, 0), 0, w - Math.max(xNow, 0), trackH);
  }
  ctx.globalAlpha = 1;

  // Time scale: grid lines + labels at a nice step for the current zoom.
  const span = dom1 - dom0;
  const step = niceStep(span, w);
  const off = -new Date(dom0).getTimezoneOffset() * 60_000;  // local-align ticks
  ctx.font = '10px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  for (let t = Math.ceil((dom0 - off) / step) * step + off; t <= dom1; t += step) {
    const x = xOf(t, w);
    ctx.strokeStyle = dim;
    ctx.globalAlpha = 0.18;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, trackH);
    ctx.stroke();
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = dim;
    const label = fmtTickLabel(t, step);
    const tw = ctx.measureText(label).width;
    ctx.fillText(label, Math.min(Math.max(x - tw / 2, 1), w - tw - 1), trackH + 2);
  }
  ctx.globalAlpha = 1;

  // Event ticks over the whole domain — activity outside the window is
  // exactly what the handles exist to reach.
  ctx.fillStyle = 'rgba(239, 68, 68, .6)';
  for (const t of liveEventTimes()) {
    if (t < dom0 || t > dom1) continue;
    const x = xOf(t, w);
    ctx.fillRect(x - 0.75, 1, 1.5, trackH * 0.42);
  }

  // The replay window: a band with grabbable edges.
  const xs = xOf(t0(), w);
  const xe = xOf(t1, w);
  ctx.fillStyle = accent;
  ctx.globalAlpha = 0.13;
  ctx.fillRect(xs, 0, xe - xs, trackH);
  ctx.globalAlpha = 0.85;
  ctx.fillRect(xs - 2.5, 2, 5, trackH - 4);
  ctx.fillRect(xe - 2.5, 2, 5, trackH - 4);
  // The move grip: a bar across the band's top (the MOVE_STRIP_PX hit zone)
  // with dots at its middle — drag it to slide the whole window.
  ctx.globalAlpha = 0.4;
  ctx.fillRect(xs + 4, 0, Math.max(xe - xs - 8, 0), 4);
  if (xe - xs > 40) {
    ctx.globalAlpha = 0.9;
    const cx = (xs + xe) / 2;
    for (const dx of [-7, 0, 7]) {
      ctx.beginPath();
      ctx.arc(cx + dx, 6.5, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  // "now" — a dashed line so the right edge of reality is always findable.
  if (xNow >= 0 && xNow <= w) {
    ctx.strokeStyle = dim;
    ctx.globalAlpha = 0.7;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(xNow, 0);
    ctx.lineTo(xNow, trackH);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  // The playhead, with a knob.
  if (playhead !== null) {
    const xp = xOf(playhead, w);
    if (xp >= -4 && xp <= w + 4) {
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(xp, 0);
      ctx.lineTo(xp, trackH);
      ctx.stroke();
      ctx.lineWidth = 1;
      ctx.fillStyle = ink;
      ctx.beginPath();
      ctx.moveTo(xp - 5, 0);
      ctx.lineTo(xp + 5, 0);
      ctx.lineTo(xp, 7);
      ctx.closePath();
      ctx.fill();
    }
  }
}

// Pick a tick step so labels sit ~90px+ apart at the current zoom.
const STEPS = [
  60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000,
  3_600_000, 2 * 3_600_000, 3 * 3_600_000, 6 * 3_600_000, 12 * 3_600_000,
  86_400_000, 2 * 86_400_000, 7 * 86_400_000, 14 * 86_400_000, 30 * 86_400_000,
];

function niceStep(span: number, w: number): number {
  for (const s of STEPS) {
    if ((s / span) * w >= 90) return s;
  }
  return STEPS[STEPS.length - 1]!;
}

function fmtTickLabel(t: number, step: number): string {
  const d = new Date(t);
  if (step >= 86_400_000) {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  // Midnight ticks carry the date so a multi-day sweep stays legible.
  return d.getHours() === 0 && d.getMinutes() === 0
    ? `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`
    : time;
}

function fmtInstant(ms: number): string {
  const d = new Date(ms);
  return dom1 - dom0 <= 26 * 3_600_000
    ? d.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// datetime-local speaks local wall time with no offset; Date parses that
// form as local, so this pair round-trips.
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function scheduleAsof(bucket: number | null): void {
  if (bucket === asofBucket) return;
  asofBucket = bucket;
  window.clearTimeout(asofTimer);
  asofTimer = window.setTimeout(() => {
    const map = mapRef;
    if (!map) return;
    setAsof(map, bucket === null
      ? null : new Date(bucket * ASOF_BUCKET_MS).toISOString());
  }, 700);
}

function setAsof(map: maplibregl.Map, asof: string | null): void {
  setEntitiesAsof(map, asof);
}
